import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import SafeIcon from '../common/SafeIcon';
import SolDivider from '../components/ui/SolDivider';
import Comments from '../components/ui/Comments';
import { EventCard, EmptyState, ClubTypeIcon } from '../components/ui/Cards';
import { motion, AnimatePresence } from 'framer-motion';

export default function ClubDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { clubs, eventos, CAPITULOS, rsvps, toggleRsvp, currentUser, addEvento, reactions, toggleReaction } = useApp();
  
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [eventError, setEventError] = useState('');
  const [eventForm, setEventForm] = useState({
    titulo: '', descripcion: '', fecha: '', modalidad: 'Presencial', ubicacion: '', enlace: ''
  });

  const club = clubs.find(c => c.id === id);
  if (!club) return <div className="p-20 text-center font-editorial text-2xl text-stone-500">Club no encontrado.</div>;

  const clubEvents = eventos
    .filter(e => e.club_id === club.id)
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

  const upcoming = clubEvents.filter(e => !e.isPast);
  const past = clubEvents.filter(e => e.isPast).reverse(); // newest past first
  const capitulo = CAPITULOS.find(c => c.id === club.capitulo_id);
  const canCreate = ['admin', 'moderador', 'colaborador'].includes(currentUser?.role);

  const clubReactions = reactions.filter(r => r.item_id === club.id);
  const hasReacted = clubReactions.some(r => r.member_id === currentUser?.id);

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    setEventError('');
    try {
      await addEvento({
        ...eventForm,
        club_id: club.id,
        capitulo_id: club.capitulo_id,
      });
      setShowCreateEvent(false);
      setEventForm({ titulo: '', descripcion: '', fecha: '', modalidad: 'Presencial', ubicacion: '', enlace: '' });
    } catch (err) {
      console.error('addEvento failed:', err);
      setEventError(err.message || 'No se pudo crear el evento');
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto space-y-10">
      <button onClick={() => navigate('/eventos')} className="group flex items-center gap-2 text-stone-400 hover:text-[#1B2A4E] transition-colors font-medium">
        <SafeIcon name="ArrowLeft" className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Volver a Eventos y Clubs
      </button>

      {/* Hero Header */}
      <div className="bg-white rounded-[40px] shadow-2xl shadow-stone-200/50 border border-stone-100 overflow-hidden relative">
        <div className="h-72 md:h-96 relative">
          <img src={club.foto_portada || club.foto_url} alt={club.nombre} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1B2A4E] via-[#1B2A4E]/60 to-transparent opacity-90"></div>
          
          <div className="absolute bottom-0 left-0 p-8 md:p-12 w-full">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-[#F6B40E] text-[#1B2A4E] px-4 py-1.5 rounded-full label-technical text-xs shadow-lg flex items-center gap-1.5">
                <ClubTypeIcon tipo={club.tipo} className="w-4 h-4" /> {club.tipo}
              </span>
              <span className="bg-white/20 backdrop-blur-md text-white border border-white/30 px-4 py-1.5 rounded-full label-technical text-xs flex items-center gap-1.5">
                <SafeIcon name="MapPin" className="w-3.5 h-3.5" /> {capitulo?.nombre}
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-editorial font-bold text-white mb-4 leading-tight">
              {club.nombre}
            </h1>
            <p className="text-white/80 text-lg max-w-2xl leading-relaxed mb-6">
              {club.descripcion}
            </p>
            
            <button 
              onClick={async () => {
                try {
                  await toggleReaction(club.id, 'club');
                } catch (err) {
                  console.error('toggleReaction failed:', err);
                }
              }} 
              className={`flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-colors w-fit shadow-lg ${hasReacted ? 'bg-red-500 text-white border border-red-400' : 'bg-white/20 backdrop-blur-md text-white border border-white/30 hover:bg-white/30'}`}
            >
              <SafeIcon name="Heart" className={`w-5 h-5 ${hasReacted ? 'fill-current' : ''}`} />
              Me interesa ({clubReactions.length})
            </button>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center px-2">
        <h2 className="text-3xl font-editorial font-bold text-[#1B2A4E]">Próximos Encuentros</h2>
        {canCreate && (
          <button onClick={() => setShowCreateEvent(true)} className="bg-[#74ACDF]/10 text-[#74ACDF] hover:bg-[#74ACDF]/20 px-5 py-2.5 rounded-xl font-bold transition-colors flex items-center gap-2 label-technical">
            <SafeIcon name="Plus" className="w-4 h-4" /> Nuevo Encuentro
          </button>
        )}
      </div>

      <div className="space-y-6">
        {upcoming.length > 0 ? upcoming.map((e, idx) => (
          <EventCard 
            key={e.id} e={e} delay={idx * 0.1} 
            capituloName={capitulo?.nombre} 
            isAttending={rsvps.some(r => r.eventId === e.id && r.memberId === currentUser.id)} 
            toggleRsvp={toggleRsvp} currentUser={currentUser} 
          />
        )) : (
          <EmptyState icon="Calendar" title="Sin encuentros programados" message="No hay próximos eventos para este club todavía." />
        )}
      </div>

      <SolDivider />

      <h2 className="text-3xl font-editorial font-bold text-[#1B2A4E] px-2">Encuentros Pasados</h2>
      <div className="space-y-6">
        {past.length > 0 ? past.map((e, idx) => (
          <div key={e.id} className="opacity-80 hover:opacity-100 transition-opacity">
            <EventCard e={e} delay={idx * 0.1} capituloName={capitulo?.nombre} isAttending={false} toggleRsvp={() => {}} currentUser={currentUser} />
          </div>
        )) : (
          <div className="text-center py-12 text-stone-400 font-editorial italic">
            El historial está vacío.
          </div>
        )}
      </div>

      {/* Comments Section */}
      <Comments itemId={club.id} itemType="club" />

      {/* Create Event Modal */}
      <AnimatePresence>
        {showCreateEvent && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setShowCreateEvent(false)} className="absolute inset-0 bg-[#1B2A4E]/60 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[32px] shadow-2xl w-full max-w-xl relative z-10 overflow-hidden border border-stone-100"
            >
              <div className="p-8 border-b border-stone-100 bg-[#FAF9F6]">
                <h2 className="text-2xl font-editorial font-bold text-[#1B2A4E]">Agendar Encuentro</h2>
                <p className="text-sm text-stone-500 mt-1">Creando un evento para "{club.nombre}".</p>
              </div>
              <form onSubmit={handleCreateEvent} className="p-8 space-y-5">
                {eventError && (
                  <div className="p-3 rounded-xl bg-red-50 text-red-600 text-sm font-medium">{eventError}</div>
                )}
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Título del Encuentro</label>
                  <input required type="text" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" placeholder="Ej: Mateada de Fin de Mes" value={eventForm.titulo} onChange={e => setEventForm({...eventForm, titulo: e.target.value})} />
                </div>
                <div>
                  <label className="block label-technical text-stone-500 mb-2">Descripción</label>
                  <textarea required rows="2" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" placeholder="Detalles del encuentro..." value={eventForm.descripcion} onChange={e => setEventForm({...eventForm, descripcion: e.target.value})} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block label-technical text-stone-500 mb-2">Fecha y Hora</label>
                    <input required type="datetime-local" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none text-stone-600" value={eventForm.fecha} onChange={e => setEventForm({...eventForm, fecha: e.target.value})} />
                  </div>
                  <div>
                    <label className="block label-technical text-stone-500 mb-2">Modalidad</label>
                    <select className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" value={eventForm.modalidad} onChange={e => setEventForm({...eventForm, modalidad: e.target.value})}>
                      <option value="Presencial">Presencial</option>
                      <option value="Virtual">Virtual</option>
                    </select>
                  </div>
                </div>
                {eventForm.modalidad === 'Presencial' ? (
                  <motion.div initial={{opacity: 0, height: 0}} animate={{opacity: 1, height: 'auto'}}>
                    <label className="block label-technical text-stone-500 mb-2">Ubicación / Dirección</label>
                    <input required type="text" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" placeholder="Ej: Central Park, NY" value={eventForm.ubicacion} onChange={e => setEventForm({...eventForm, ubicacion: e.target.value})} />
                  </motion.div>
                ) : (
                  <motion.div initial={{opacity: 0, height: 0}} animate={{opacity: 1, height: 'auto'}}>
                    <label className="block label-technical text-stone-500 mb-2">Enlace de Videollamada</label>
                    <input required type="url" className="w-full p-4 rounded-xl bg-stone-50 border border-stone-200 focus:ring-2 focus:ring-[#74ACDF]/50 outline-none" placeholder="https://zoom.us/..." value={eventForm.enlace} onChange={e => setEventForm({...eventForm, enlace: e.target.value})} />
                  </motion.div>
                )}
                <div className="pt-4 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowCreateEvent(false)} className="px-6 py-3 rounded-xl font-bold text-stone-500 hover:bg-stone-100 transition-colors">Cancelar</button>
                  <button type="submit" className="px-8 py-3 rounded-xl font-bold bg-[#1B2A4E] text-white hover:bg-[#253969] transition-colors shadow-md">Agendar</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}